import type { AxiosInstance } from "axios";
import { EMethod } from "@bc-library/interface";
import { API_MAIN_VERSION, BASE_URL } from "@/config/constantes";
import useAxios from "@/services/api/useAxios";

const resource = "signatureStats";
const base = `${BASE_URL}/${API_MAIN_VERSION}/${resource}`;

export type SignatureServerType = "shared" | "dedicated";
export type SignatureClientStatus = "active" | "former" | "all";
export type SignatureGranularity = "day" | "week" | "month";
export type SignatureOrigin = "onboarding" | "offboarding" | "campaign" | "other";
export type SignatureStatus = "finished" | "pending" | "refused" | "expired";

export const SIGNATURE_ORIGINS: SignatureOrigin[] = ["onboarding", "offboarding", "campaign", "other"];
export const SIGNATURE_STATUSES: SignatureStatus[] = ["finished", "pending", "refused", "expired"];

export type SignatureFirmSummary = {
  _id: string;
  company: string;
  code?: string;
  isDedicatedServer: boolean;
  isFormerClient?: boolean;
  /** The e-signature option of its subscription, as its own server reads it. */
  eSignature?: boolean;
};

/** Documents sent over the period, by what became of them since. */
export type SignatureSentCounts = Record<SignatureStatus, number> & { total: number };

export type SignatureFirmRow = {
  firm: SignatureFirmSummary;
  /** Signatures finished over the period (dated by their completion): what the consumption bills. */
  finished: number;
  finishedByOrigin: Record<SignatureOrigin, number>;
  sent: SignatureSentCounts;
  completionRate: number | null;
  lastFinishedAt: string | null;
  connected: boolean;
  error?: string;
};

export type SignatureTotals = {
  firms: number;
  activeFirms: number;
  unreachableFirms: number;
  finished: number;
  finishedByOrigin: Record<SignatureOrigin, number>;
  sent: SignatureSentCounts;
  completionRate: number | null;
};

export type SignatureSeriesPoint = {
  /** `YYYY-MM` for a month, the Monday `YYYY-MM-DD` for a week, the day otherwise. */
  bucket: string;
  finished: number;
  sent: SignatureSentCounts;
  /** Finished signatures of the bucket per firm id. */
  byFirm: Record<string, number>;
};

export type SignatureOverviewResponse = {
  generatedAt: string;
  startDate: string;
  endDate: string;
  timezone: string;
  /** The scale used: moved up when the period would draw too many points. */
  granularity: SignatureGranularity;
  buckets: string[];
  totals: SignatureTotals;
  firms: SignatureFirmRow[];
  series: SignatureSeriesPoint[];
};

export type SignaturePerson = { firstname: string; lastname: string; email?: string };

export type SignatureListItem = {
  _id: string;
  firmId: string;
  firm: SignatureFirmSummary;
  display: string;
  origin: SignatureOrigin;
  campaign: string | null;
  launchedBy: SignaturePerson | null;
  signer: SignaturePerson | null;
  coSigners: SignaturePerson[];
  sentAt: string | null;
  finishedAt: string;
};

export type SignatureListResponse = {
  startDate: string;
  endDate: string;
  items: SignatureListItem[];
  total: number;
  /** More finished signatures than one list holds (20 000). */
  truncated: boolean;
  unreachableFirms: SignatureFirmSummary[];
};

export type SignatureFilters = {
  firmIds?: string;
  serverType?: SignatureServerType;
  clientStatus?: SignatureClientStatus;
  origin?: SignatureOrigin;
  startDate?: string;
  endDate?: string;
};

const listFirms = (axios: any) => (query: { clientStatus?: SignatureClientStatus } = {}) =>
  axios({
    method: EMethod.get,
    url: `${base}/admin/firms`,
    query,
  });

const getOverview =
  (axios: any) =>
  (query: SignatureFilters & { granularity?: SignatureGranularity; timezone?: string }) =>
    axios({
      method: EMethod.get,
      url: `${base}/admin/overview`,
      query,
    });

const listSignatures = (axios: any) => (query: SignatureFilters) =>
  axios({
    method: EMethod.get,
    url: `${base}/admin/list`,
    query,
  });

/** Finished signatures of one firm, for its consumption export. */
const listFirmSignatures =
  (axios: any) => (firmId: string, query: { startDate: string; endDate: string }) =>
    axios({
      method: EMethod.get,
      url: `${base}/firms/${firmId}`,
      query,
    });

export default (axios: AxiosInstance) => ({
  listFirms: listFirms(useAxios(axios)),
  getOverview: getOverview(useAxios(axios)),
  listSignatures: listSignatures(useAxios(axios)),
  listFirmSignatures: listFirmSignatures(useAxios(axios)),
});
